"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import useScrollDirection from "@/tools/hooks/useScrollDirection";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useLenis } from "@/components/LenisProvider";

interface HeaderState {
  showSearch: boolean;
  setShowSearch: (showSearch: boolean) => void;
  mobileNavOpen: boolean;
  setMobileNavOpen: (mobileNavOpen: boolean) => void;
  hidden: boolean;
  atTop: boolean;
}

const useHeaderState = (): HeaderState => {
  const pathname = usePathname();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const [hidden, setHidden] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const scrollDir = useScrollDirection();
  const isMobile = useMediaQuery({ down: "smallDesktop" });
  const lenis = useLenis();

  // Hide header on scroll down; close desktop search on scroll
  useEffect(() => {
    const initialScroll = window.scrollY;
    const handleScroll = () => {
      const scrollingUp = scrollDir === "up";
      const THRESHOLD = 100;
      const scrolledPastThreshold = window.scrollY > THRESHOLD;
      const shouldHide = scrolledPastThreshold && !scrollingUp && !mobileNavOpen && !showSearch;
      setHidden(shouldHide);
      setAtTop(!scrolledPastThreshold);

      const SEARCH_CLOSE_THRESHOLD = 50;
      if (
        showSearch &&
        !isMobile &&
        Math.abs(window.scrollY - initialScroll) > SEARCH_CLOSE_THRESHOLD
      ) {
        setShowSearch(false);
      }
    };

    // Set initial state without closing search
    const scrolledPastThreshold = initialScroll > 100;
    setAtTop(!scrolledPastThreshold);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollDir, showSearch, isMobile]);

  // Close mobile nav when navigating to a new page
  useEffect(() => {
    setMobileNavOpen(false);
    setShowSearch(false);
    document.body.style.overflow = "";
    delete document.body.dataset.lenisPrevent;
  }, [pathname]);

  // Prevent scrolling while mobile search or mobile nav is open
  useEffect(() => {
    const shouldLock = mobileNavOpen || (showSearch && isMobile);
    if (shouldLock) {
      document.body.style.overflow = "clip";
      document.body.dataset.lenisPrevent = "true";
      lenis.stop();
      setHidden(false);
    } else {
      document.body.style.overflow = "";
      delete document.body.dataset.lenisPrevent;
      lenis.start();
    }
  }, [showSearch, mobileNavOpen, isMobile, lenis]);

  // Close mobile nav when search is opened
  useEffect(() => {
    if (showSearch) {
      setMobileNavOpen(false);
    }
  }, [showSearch]);

  // Close search when mobile nav is opened
  useEffect(() => {
    if (mobileNavOpen) {
      setShowSearch(false);
    }
  }, [mobileNavOpen]);

  return {
    atTop,
    hidden,
    mobileNavOpen,
    setMobileNavOpen,
    setShowSearch,
    showSearch,
  };
};

export default useHeaderState;
